import { useRef, useState, useEffect } from "react";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import TextField from "@mui/material/TextField";
import DeleteIcon from "@mui/icons-material/Delete";
import Typography from "@mui/material/Typography";
import { useBranding, useUploadLogo, useUploadLogoDark, useDeleteLogo, useDeleteLogoDark, useUpdateOpportunitiesLabel } from "@/hooks/queries";
import { useNotification } from "@/hooks/useNotification";
import styles from "./styles.module.css";

export const BrandingSection = () => {
  const { data: branding } = useBranding();
  const uploadLogo = useUploadLogo();
  const uploadLogoDark = useUploadLogoDark();
  const deleteLogo = useDeleteLogo();
  const deleteLogoDark = useDeleteLogoDark();
  const updateLabel = useUpdateOpportunitiesLabel();
  const { notifySuccess, notifyError } = useNotification();
  const lightRef = useRef<HTMLInputElement>(null);
  const darkRef = useRef<HTMLInputElement>(null);
  const [svgContent, setSvgContent] = useState<string | null>(null);
  const [labelValue, setLabelValue] = useState("");
  const [labelDirty, setLabelDirty] = useState(false);

  useEffect(() => {
    if (!labelDirty) {
      setLabelValue(branding?.opportunities_label ?? "");
    }
  }, [branding?.opportunities_label, labelDirty]);

  useEffect(() => {
    if (branding?.logo_is_svg && branding.logo_url) {
      fetch(branding.logo_url)
        .then((r) => r.text())
        .then(setSvgContent)
        .catch(() => setSvgContent(null));
    } else {
      setSvgContent(null);
    }
  }, [branding?.logo_is_svg, branding?.logo_url]);

  const handleUpload = (type: "light" | "dark") => async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      if (type === "light") {
        await uploadLogo.mutateAsync(file);
      } else {
        await uploadLogoDark.mutateAsync(file);
      }
      notifySuccess("Logo uploaded");
    } catch {
      notifyError("Failed to upload logo");
    }
    e.target.value = "";
  };

  const handleDelete = (type: "light" | "dark") => async () => {
    try {
      if (type === "light") {
        await deleteLogo.mutateAsync();
      } else {
        await deleteLogoDark.mutateAsync();
      }
      notifySuccess("Logo removed");
    } catch {
      notifyError("Failed to remove logo");
    }
  };

  const renderPreview = () => {
    if (branding?.logo_is_svg && svgContent) {
      return (
        <span
          className={styles.logoPreviewSvg}
          dangerouslySetInnerHTML={{ __html: svgContent }}
        />
      );
    }
    if (branding?.logo_url) {
      return <img src={branding.logo_url} alt="Logo" className={styles.logoPreview} />;
    }
    return <Typography color="text.secondary">No logo uploaded</Typography>;
  };

  const isSvg = branding?.logo_is_svg ?? false;

  return (
    <div className={styles.section}>
      <h2 className={styles.sectionTitle}>Branding</h2>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Upload your company logo. SVG files are recommended — they automatically adapt to light and dark themes via CSS. PNG/JPG files require a separate dark mode upload.
      </Typography>
      <div className={styles.uploadArea}>
        <div className={styles.uploadBox}>
          <Typography variant="subtitle2">
            {isSvg ? "Logo (SVG)" : "Logo (Light Mode)"}
          </Typography>
          {isSvg && (
            <Typography variant="caption" color="success.main">
              Auto light/dark theme
            </Typography>
          )}
          {renderPreview()}
          <div>
            <input
              ref={lightRef}
              type="file"
              accept=".png,.jpg,.jpeg,.svg"
              hidden
              onChange={handleUpload("light")}
            />
            <Button
              size="small"
              variant="outlined"
              onClick={() => lightRef.current?.click()}
              disabled={uploadLogo.isPending}
            >
              Upload
            </Button>
            {branding?.logo_url && (
              <IconButton
                size="small"
                color="error"
                onClick={handleDelete("light")}
                disabled={deleteLogo.isPending}
                aria-label="Delete logo"
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            )}
          </div>
        </div>
        {!isSvg && (
          <div className={styles.uploadBox}>
            <Typography variant="subtitle2">Logo (Dark Mode)</Typography>
            <Typography variant="caption" color="text.secondary">
              Optional
            </Typography>
            {branding?.logo_dark_url ? (
              <img src={branding.logo_dark_url} alt="Dark logo" className={styles.logoPreview} />
            ) : (
              <Typography color="text.secondary">No dark logo</Typography>
            )}
            <div>
              <input
                ref={darkRef}
                type="file"
                accept=".png,.jpg,.jpeg"
                hidden
                onChange={handleUpload("dark")}
              />
              <Button
                size="small"
                variant="outlined"
                onClick={() => darkRef.current?.click()}
                disabled={uploadLogoDark.isPending || !branding?.logo_url}
              >
                Upload
              </Button>
              {branding?.logo_dark_url && (
                <IconButton
                  size="small"
                  color="error"
                  onClick={handleDelete("dark")}
                  disabled={deleteLogoDark.isPending}
                  aria-label="Delete dark logo"
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              )}
            </div>
          </div>
        )}
      </div>
      <div className={styles.uploadBox} style={{ marginTop: 24 }}>
        <Typography variant="subtitle2">Opportunities Button Label</Typography>
        <Typography variant="caption" color="text.secondary">
          Customizes the toggle button text on the main page. Leave empty for default.
        </Typography>
        <div style={{ display: "flex", gap: 8, alignItems: "flex-start" }}>
          <TextField
            size="small"
            value={labelValue}
            onChange={(e) => { setLabelValue(e.target.value); setLabelDirty(true); }}
            placeholder="Touring and Show Listings"
            inputProps={{ maxLength: 100 }}
            sx={{ width: 300 }}
          />
          <Button
            size="small"
            variant="outlined"
            disabled={!labelDirty || updateLabel.isPending}
            onClick={async () => {
              try {
                await updateLabel.mutateAsync(labelValue || null);
                setLabelDirty(false);
                notifySuccess("Label updated");
              } catch {
                notifyError("Failed to update label");
              }
            }}
          >
            Save
          </Button>
        </div>
      </div>
    </div>
  );
};
